<template>
  <div class="solutions-page">
    <Navbar :currentLang="currentLang" @change-lang="changeLang" />

    <!-- Hero -->
    <section class="sol-hero">
      <div class="sol-hero-inner">
        <div class="sol-hero-text" data-aos="fade-up">
          <span class="section-eyebrow">Solutions</span>
          <h1 class="sol-hero-title">Solutions Tailored to<br>Your Business Model</h1>
          <p class="sol-hero-sub">Different sellers prioritize different Key Results. Pick the model that matches your stage, margin target, and fulfillment complexity.</p>
          <div class="sol-hero-btns">
            <button class="btn-primary" @click="scrollToContact">Get a Solution</button>
            <button class="btn-ghost" @click="scrollToContact">
              <svg viewBox="0 0 24 24" fill="currentColor" style="width:18px;height:18px;"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.119.554 4.107 1.523 5.83L0 24l6.338-1.498A11.96 11.96 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.854 0-3.59-.5-5.086-1.373l-.361-.214-3.762.889.941-3.667-.235-.375A9.945 9.945 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
              Talk to an Expert
            </button>
          </div>
        </div>
        <div class="sol-hero-visual" data-aos="fade-left">
          <div class="sol-hero-img-wrap">
            <img src="https://static.wixstatic.com/media/575a2a_67257af414b14ce4a7941062339a4bc2f000.jpg/v1/fill/w_800,h_500,al_c,q_85,enc_avif,quality_auto/575a2a_67257af414b14ce4a7941062339a4bc2f000.jpg" alt="Warehouse Operations" />
            <div class="sol-hero-img-badge">
              <span class="img-badge-dot"></span>
              Live Fulfillment Center
            </div>
          </div>
          <div class="sol-hero-float-card">
            <div class="float-icon--green">
              <i class="fa-solid fa-boxes-stacked"></i>
            </div>
            <div>
              <div class="float-num">50K+</div>
              <div class="float-label">Orders/Day</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Trust Bar -->
    <div class="trust-bar">
      <div class="trust-bar-inner">
        <div class="trust-stat" v-for="(s, i) in trustStats" :key="i">
          <span class="trust-stat-num">{{ s.num }}</span>
          <span class="trust-stat-label">{{ s.label }}</span>
        </div>
      </div>
    </div>

    <!-- Solutions Cards Section (with real images) -->
    <section class="sol-cards-section">
      <div class="sol-section-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">Core Solutions</span>
          <h2 class="section-title">{{ t.businessModels.title }}</h2>
          <p class="section-desc">{{ t.businessModels.subtitle }}</p>
        </div>
        <div class="sol-main-grid">
          <div
            class="sol-main-card"
            v-for="(item, index) in t.businessModels.items"
            :key="index"
            :class="index === 0 ? 'sol-main-card--featured' : ''"
            data-aos="fade-up"
            :data-aos-delay="index * 60"
          >
            <div class="sol-main-card-img">
              <img :src="getSolutionImage(index)" :alt="item.title" />
            </div>
            <div class="sol-main-card-body">
              <div class="sol-main-card-icon" :class="'sol-icon--' + index">
                <i :class="['fa-solid', 'fa-' + getBizIcon(index)]"></i>
              </div>
              <div class="sol-main-card-label">Solution {{ String(index + 1).padStart(2, '0') }}</div>
              <h3 class="sol-main-title">{{ item.title }}</h3>
              <p class="sol-main-desc">{{ item.description }}</p>
              <div class="sol-main-tags">
                <span class="sol-tag" v-for="(tag, ti) in getSolutionTags(index)" :key="ti">{{ tag }}</span>
              </div>
              <a class="sol-main-cta" href="#" @click.prevent="scrollToContact">
                Learn More
                <svg viewBox="0 0 20 20" fill="currentColor" style="width:16px;height:16px;"><path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- How We Build Your Supply Chain -->
    <section class="build-section">
      <div class="sol-section-inner">
        <div class="build-two-col">
          <div class="build-col" data-aos="fade-right">
            <span class="section-eyebrow" style="margin-bottom:1rem;display:inline-block;">System Setup</span>
            <h2 class="section-title" style="text-align:left;font-size:2rem;">How FULFILLDROP Builds Your Supply Chain System</h2>
            <div class="build-steps">
              <div class="build-step" v-for="(step, si) in buildSteps" :key="si">
                <div class="build-step-num">{{ String(si+1).padStart(2,'0') }}</div>
                <div class="build-step-body">
                  <div class="build-step-title">{{ step.title }}</div>
                  <div class="build-step-desc">{{ step.desc }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="build-col" data-aos="fade-left">
            <div class="build-col-img">
              <img src="https://static.wixstatic.com/media/575a2a_3f47341d16ca4f4b9e2f8ed488e08070~mv2.jpg/v1/fit/w_600,h_400,q_90,enc_avif,quality_auto/575a2a_3f47341d16ca4f4b9e2f8ed488e08070~mv2.jpg" alt="Supply Chain Dashboard" />
            </div>
            <div class="build-col-steps">
              <span class="section-eyebrow" style="margin-bottom:1rem;display:inline-block;">Quick Start</span>
              <h2 class="section-title" style="text-align:left;font-size:2rem;">Get Started in Minutes</h2>
              <div class="build-steps">
                <div class="build-step" v-for="(step, si) in startSteps" :key="'b'+si">
                  <div class="build-step-num">{{ String(si+1).padStart(2,'0') }}</div>
                  <div class="build-step-body">
                    <div class="build-step-title">{{ step.title }}</div>
                    <div class="build-step-desc">{{ step.desc }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Industry Expertise (with real images) -->
    <section class="industry-section">
      <div class="sol-section-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">Industry Expertise</span>
          <h2 class="section-title">Deep Specialization in Key Categories</h2>
          <p class="section-desc">We don't just handle logistics — we understand your products, your quality standards, and your customers' expectations.</p>
        </div>
        <div class="industry-grid">
          <div class="industry-card" data-aos="fade-up" data-aos-delay="0">
            <div class="industry-card-img">
              <img src="https://static.wixstatic.com/media/575a2a_c27a7b2d3f9847678568c50ea6d06810~mv2.png/v1/fill/w_400,h_250,al_c,q_85,enc_avif,quality_auto/2.png" alt="Fashion & Apparel" />
              <div class="industry-overlay">
                <span class="industry-tag">Fashion & Apparel</span>
              </div>
            </div>
            <div class="industry-card-body">
              <h4>Fashion & Apparel</h4>
              <p>From sizing consistency to fabric quality control, we handle the complexities of fashion fulfillment with precision.</p>
              <div class="industry-tags">
                <span>Size QC</span><span>Fabric Check</span><span>Custom Labels</span>
              </div>
            </div>
          </div>
          <div class="industry-card" data-aos="fade-up" data-aos-delay="100">
            <div class="industry-card-img">
              <img src="https://static.wixstatic.com/media/575a2a_b0c624181227498085cedc6fbfe10638~mv2.png/v1/fill/w_400,h_250,al_c,q_85,enc_avif,quality_auto/%E8%A1%A5%E5%89%82.png" alt="Health Supplements" />
              <div class="industry-overlay">
                <span class="industry-tag">Health Supplements</span>
              </div>
            </div>
            <div class="industry-card-body">
              <h4>Health Supplements</h4>
              <p>Compliant packaging, batch tracking, and temperature-controlled storage for supplements and wellness products.</p>
              <div class="industry-tags">
                <span>Batch Tracking</span><span>Compliance</span><span>Certified Storage</span>
              </div>
            </div>
          </div>
          <div class="industry-card" data-aos="fade-up" data-aos-delay="200">
            <div class="industry-card-img">
              <img src="https://static.wixstatic.com/media/575a2a_f2ac3c53f08d4322a3a7dc238155d975~mv2.png/v1/fill/w_400,h_250,al_c,q_85,enc_avif,quality_auto/ChatGPT%20Image%202026%E5%B9%B41%E6%9C%8830%E6%97%A5%2017_05_41.png" alt="Lighting Products" />
              <div class="industry-overlay">
                <span class="industry-tag">Lighting & Home</span>
              </div>
            </div>
            <div class="industry-card-body">
              <h4>Lighting & Home Decor</h4>
              <p>Specialized handling for fragile, high-value items with protective packaging and insurance options.</p>
              <div class="industry-tags">
                <span>Fragile Handling</span><span>Insurance</span><span>Protective Packaging</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Case Study Preview -->
    <section class="case-preview-section">
      <div class="sol-section-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">{{ t.caseStudy.title }}</span>
          <h2 class="section-title">{{ t.caseStudy.subtitle }}</h2>
        </div>
        <div class="case-preview-card" data-aos="fade-up">
          <div class="case-preview-img">
            <img src="https://static.wixstatic.com/media/575a2a_83df7795f2f64b4db8fd052e30d61870~mv2.png/v1/fill/w_500,h_350,al_c,q_85,enc_avif,quality_auto/3.png" alt="Case Study" />
          </div>
          <div class="case-preview-body">
            <span class="case-badge">{{ t.caseStudy.badge }}</span>
            <h3 class="case-preview-title">{{ t.caseStudy.caseTitle }}</h3>
            <p class="case-preview-intro">{{ t.caseStudy.intro }}</p>
            <h5 class="case-factors-title">{{ t.caseStudy.factors.title }}</h5>
            <ul class="case-factors-list">
              <li v-for="(f, fi) in t.caseStudy.factors.items" :key="fi">
                <svg class="check-icon-green" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                {{ f }}
              </li>
            </ul>
            <button class="btn-primary" @click="scrollToContact">{{ t.caseStudy.btn }}</button>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="sol-cta-section">
      <div class="sol-cta-inner" data-aos="fade-up">
        <h2 class="sol-cta-title">Ready to build a supply chain system<br>that scales with your business?</h2>
        <p class="sol-cta-sub">No subscription fees. No MOQs. Get started anytime.</p>
        <div class="sol-cta-btns">
          <button class="btn-primary btn-primary--lg" @click="scrollToContact">Get a Solution</button>
          <button class="btn-ghost btn-ghost--invert btn-ghost--lg" @click="scrollToContact">Talk to an Expert</button>
        </div>
      </div>
    </section>

    <Footer :currentLang="currentLang" />
  </div>
</template>

<script>
import Navbar from '../components/Navbar.vue'
import Footer from '../components/Footer.vue'
import { languages } from '../assets/languages.js'

export default {
  name: 'Solutions',
  props: { currentLang: { type: String, default: 'en' } },
  components: { Navbar, Footer },
  data() {
    return {
      trustStats: [
        { num: '1.7M+', label: 'SKUs' },
        { num: '98%', label: '24h Dispatch Rate' },
        { num: '10K+', label: 'Orders/Day Capacity' },
        { num: '28', label: 'Sourcing Experts' },
        { num: '5–12 days', label: 'Global Delivery' },
      ],
      buildSteps: [
        { title: 'Define your seller profile and growth goals', desc: 'We analyze your business model, product category, and scale to understand your needs.' },
        { title: 'Map your channels, SKU structure, and target markets', desc: 'Identify where you sell, what you sell, and who you sell to.' },
        { title: 'Design an execution-ready operating plan', desc: 'Create a tailored supply chain blueprint covering sourcing, fulfillment, and logistics.' },
        { title: 'Run execution through one unified system', desc: 'Continuously optimize with data and dedicated team support.' },
      ],
      startSteps: [
        { title: 'Create your FULFILLDROP account', desc: 'Sign up in minutes — no upfront fees or lengthy contracts.' },
        { title: 'Connect your store', desc: 'Integrate with Shopify, WooCommerce, Amazon, or other platforms.' },
        { title: 'Add products to your store', desc: 'Source from 1.7M+ SKUs or bring your own products.' },
        { title: 'Manage orders efficiently', desc: 'Automate order sync, fulfillment, and tracking in one dashboard.' },
      ]
    }
  },
  computed: {
    t() { return languages[this.currentLang] || languages.en }
  },
  methods: {
    changeLang(lang) { this.$emit('change-lang', lang) },
    getBizIcon(index) {
      return ['truck-fast', 'flag', 'box-open', 'warehouse', 'palette'][index] || 'star'
    },
    getOsIcon(index) {
      return ['truck-fast', 'palette', 'warehouse', 'truck', 'shield-check', 'headset'][index] || 'star'
    },
    getSolutionTags(index) {
      const tags = [
        ['Stable Sourcing', 'Scalable Execution', 'Fast Dispatch'],
        ['Brand Consistency', 'OEM / ODM', 'DTC Ready'],
        ['High Volume', 'Accuracy First', 'Cost Control'],
        ['3PL', 'Multi-Channel', 'Inventory Visibility'],
        ['POD', 'Custom Packaging', 'Bespoke Scale'],
      ]
      return tags[index] || []
    },
    getSolutionImage(index) {
      const imgs = [
        'https://static.wixstatic.com/media/575a2a_a46e4c3252d346789313e3480eae0081~mv2.png/v1/fill/w_600,h_400,al_c,q_85,enc_avif,quality_auto/575a2a_a46e4c3252d346789313e3480eae0081~mv2.png',
        'https://static.wixstatic.com/media/575a2a_812d252af6ea443aba66d66751eae5f0~mv2.png/v1/fill/w_600,h_400,al_c,q_85,enc_avif,quality_auto/575a2a_812d252af6ea443aba66d66751eae5f0~mv2.png',
        'https://static.wixstatic.com/media/575a2a_9661c470e6c64d31bc14fb2946703ea2~mv2.png/v1/fill/w_600,h_400,al_c,q_85,enc_avif,quality_auto/575a2a_9661c470e6c64d31bc14fb2946703ea2~mv2.png',
        'https://static.wixstatic.com/media/575a2a_67257af414b14ce4a7941062339a4bc2f000.jpg/v1/fill/w_600,h_400,al_c,q_85,enc_avif,quality_auto/575a2a_67257af414b14ce4a7941062339a4bc2f000.jpg',
        'https://static.wixstatic.com/media/575a2a_30b96448cffb4c349febe01c7efc6a15~mv2.jpg/v1/fill/w_600,h_400,al_c,q_85,enc_avif,quality_auto/Cross-Border%20Operations%20Team_edited.jpg',
      ]
      return imgs[index] || imgs[0]
    },
    scrollToContact() {
      this.$router.push('/').then(() => {
        setTimeout(() => {
          const el = document.getElementById('contact')
          if (el) el.scrollIntoView({ behavior: 'smooth' })
        }, 300)
      })
    }
  }
}
</script>

<style scoped>
/* ============================================================
   BASE
   ============================================================ */
.solutions-page {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #0f172a;
  background: #fff;
  overflow-x: hidden;
}
.sol-section-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}
.section-header {
  text-align: center;
  margin-bottom: 4rem;
}
.section-eyebrow {
  display: inline-block;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #28b5c1;
  background: rgba(40,181,193,0.08);
  padding: 0.35rem 1rem;
  border-radius: 999px;
  margin-bottom: 1rem;
}
.section-title {
  font-size: 2.6rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.15;
  margin-bottom: 1rem;
}
.section-desc {
  font-size: 1.1rem;
  color: #64748b;
  max-width: 680px;
  margin: 0 auto;
  line-height: 1.75;
}
.btn-primary {
  display: inline-flex; align-items: center; gap: 0.5rem;
  background: #28b5c1; color: #fff;
  border: none; padding: 0.9rem 2rem;
  border-radius: 10px; font-weight: 600; font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s, transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 4px 16px rgba(40,181,193,0.28);
}
.btn-primary:hover { background: #1e9aa5; transform: translateY(-2px); }
.btn-primary--lg { padding: 1.1rem 2.5rem; font-size: 1.1rem; }
.btn-ghost {
  display: inline-flex; align-items: center; gap: 0.5rem;
  background: transparent; color: #0f172a;
  border: 1.5px solid #cbd5e1; padding: 0.9rem 2rem;
  border-radius: 10px; font-weight: 600; font-size: 1rem;
  cursor: pointer; transition: border-color 0.2s, background 0.2s;
}
.btn-ghost:hover { border-color: #28b5c1; color: #28b5c1; }
.btn-ghost--invert { border-color: rgba(255,255,255,0.5); color: #fff; }
.btn-ghost--invert:hover { background: rgba(255,255,255,0.1); border-color: #fff; color: #fff; }
.btn-ghost--lg { padding: 1.1rem 2.5rem; font-size: 1.1rem; }

/* ============================================================
   HERO
   ============================================================ */
.sol-hero {
  background: linear-gradient(160deg, #f0f4ff 0%, #fafafa 60%, #fff 100%);
  padding: 6rem 0 0;
  overflow: hidden;
}
.sol-hero-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem 5rem;
  display: flex;
  align-items: center;
  gap: 4rem;
}
.sol-hero-text { flex: 1; }
.sol-hero-title {
  font-size: clamp(2.2rem, 3.5vw, 3.2rem);
  font-weight: 900;
  color: #0f172a;
  line-height: 1.1;
  margin-bottom: 1.2rem;
}
.sol-hero-sub {
  font-size: 1.1rem;
  color: #475569;
  line-height: 1.7;
  max-width: 500px;
  margin-bottom: 2.2rem;
}
.sol-hero-btns {
  display: flex; flex-wrap: wrap; gap: 1rem;
}

/* Hero Visual */
.sol-hero-visual {
  flex: 0 0 520px;
  position: relative;
}
.sol-hero-img-wrap {
  width: 100%;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 32px 64px rgba(0,0,0,0.15);
  position: relative;
}
.sol-hero-img-wrap img {
  width: 100%;
  height: auto;
  display: block;
  aspect-ratio: 16/10;
  object-fit: cover;
}
.sol-hero-img-badge {
  position: absolute;
  bottom: 14px; left: 14px;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(8px);
  color: #fff;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  display: flex; align-items: center; gap: 0.4rem;
}
.sol-hero-float-card {
  position: absolute;
  bottom: 30px; right: -20px;
  background: rgba(255,255,255,0.95);
  backdrop-filter: blur(12px);
  border-radius: 14px;
  padding: 0.9rem 1.2rem;
  box-shadow: 0 12px 32px rgba(0,0,0,0.2);
  display: flex; align-items: center; gap: 0.8rem;
  min-width: 180px;
  border: 1px solid rgba(255,255,255,0.3);
  animation: floatCard 3s ease-in-out infinite;
}
@keyframes floatCard {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}
.float-icon--green { background: #dcfce7; color: #16a34a; width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 1.1rem; }
.float-num { font-size: 1.2rem; font-weight: 800; color: #0f172a; line-height: 1; }
.float-label { font-size: 0.72rem; color: #64748b; margin-top: 0.15rem; }

/* Trust Bar */
.trust-bar {
  background: #0f172a;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  padding: 2rem 0;
}
.trust-bar-inner {
  max-width: 1200px; margin: 0 auto; padding: 0 2rem;
  display: flex; justify-content: space-around; flex-wrap: wrap; gap: 1rem;
}
.trust-stat { text-align: center; display: flex; flex-direction: column; gap: 0.3rem; }
.trust-stat-num { font-size: 1.6rem; font-weight: 800; color: #fff; }
.trust-stat-label { font-size: 0.78rem; color: #94a3b8; font-weight: 500; }

/* ============================================================
   SOLUTIONS CARDS (with real images)
   ============================================================ */
.sol-cards-section {
  padding: 7rem 0;
  background: #fff;
}
.sol-main-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}
.sol-main-card {
  border: 1.5px solid #e2e8f0;
  border-radius: 18px;
  padding: 0;
  background: #fff;
  display: flex; flex-direction: column;
  transition: box-shadow 0.25s, transform 0.25s, border-color 0.25s;
  overflow: hidden;
}
.sol-main-card:hover {
  box-shadow: 0 20px 50px rgba(15,23,42,0.1);
  transform: translateY(-5px);
  border-color: #a5b4fc;
}
.sol-main-card--featured {
  border-color: #28b5c1;
  background: linear-gradient(160deg, #f0f4ff 0%, #fff 100%);
}
.sol-main-card--featured::before {
  content: 'Most Popular';
  display: inline-block;
  position: absolute;
  top: -12px; right: 20px;
  background: #28b5c1; color: #fff;
  font-size: 0.72rem; font-weight: 700;
  padding: 0.25rem 0.8rem; border-radius: 999px;
}
.sol-main-card { position: relative; }
.sol-main-card-img {
  width: 100%;
  height: 220px;
  overflow: hidden;
  background: #f8fafc;
}
.sol-main-card-img img {
  width: 100%; height: 100%; object-fit: contain; padding: 1rem;
  transition: transform 0.4s;
}
.sol-main-card:hover .sol-main-card-img img { transform: scale(1.05); }
.sol-main-card-body { padding: 1.5rem 2rem 2rem; flex: 1; display: flex; flex-direction: column; }
.sol-main-card-icon {
  width: 48px; height: 48px;
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.3rem; color: #fff;
  margin-bottom: 0.8rem;
}
.sol-icon--0 { background: linear-gradient(135deg,#28b5c1,#818cf8); }
.sol-icon--1 { background: linear-gradient(135deg,#0891b2,#38bdf8); }
.sol-icon--2 { background: linear-gradient(135deg,#16a34a,#4ade80); }
.sol-icon--3 { background: linear-gradient(135deg,#d97706,#fbbf24); }
.sol-icon--4 { background: linear-gradient(135deg,#dc2626,#f87171); }
.sol-main-card-label {
  font-size: 0.72rem; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase;
  color: #94a3b8; margin-bottom: 0.5rem;
}
.sol-main-title {
  font-size: 1.15rem; font-weight: 700;
  color: #0f172a; margin-bottom: 0.7rem; line-height: 1.3;
}
.sol-main-desc {
  font-size: 0.88rem; color: #64748b;
  line-height: 1.65; flex: 1; margin-bottom: 1.2rem;
}
.sol-main-tags { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-bottom: 1.5rem; }
.sol-tag {
  font-size: 0.72rem; font-weight: 600;
  color: #28b5c1; background: rgba(40,181,193,0.08);
  border-radius: 999px; padding: 0.2rem 0.7rem;
}
.sol-main-cta {
  display: inline-flex; align-items: center; gap: 0.4rem;
  font-size: 0.9rem; font-weight: 600;
  color: #28b5c1; text-decoration: none;
  transition: gap 0.2s; margin-top: auto;
}
.sol-main-cta:hover { text-decoration: underline; }

/* ============================================================
   UNIFIED SYSTEM
   ============================================================ */
.unified-section {
  padding: 7rem 0;
  background: linear-gradient(160deg, #0f172a 0%, #1e1b4b 100%);
}
.unified-section .section-eyebrow { background: rgba(255,255,255,0.08); color: #a5b4fc; }
.unified-section .section-title { color: #fff; }
.unified-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}
.unified-card {
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px; padding: 2rem;
  transition: background 0.25s, transform 0.25s, border-color 0.25s;
}
.unified-card:hover {
  background: rgba(255,255,255,0.08);
  border-color: rgba(165,180,252,0.35);
  transform: translateY(-4px);
}
.unified-icon {
  width: 48px; height: 48px;
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.25rem; margin-bottom: 1.2rem;
}
.unified-icon--0 { background: rgba(124,58,237,0.25); color: #c4b5fd; }
.unified-icon--1 { background: rgba(14,165,233,0.25); color: #7dd3fc; }
.unified-icon--2 { background: rgba(34,197,94,0.25); color: #86efac; }
.unified-icon--3 { background: rgba(249,115,22,0.25); color: #fdba74; }
.unified-icon--4 { background: rgba(239,68,68,0.25); color: #fca5a5; }
.unified-icon--5 { background: rgba(20,184,166,0.25); color: #5eead4; }
.unified-title {
  font-size: 1rem; font-weight: 700;
  color: #f1f5f9; margin-bottom: 0.6rem;
}
.unified-desc {
  font-size: 0.88rem; color: #94a3b8;
  line-height: 1.65; margin-bottom: 1rem;
}
.unified-tags { display: flex; flex-wrap: wrap; gap: 0.4rem; }
.unified-tag {
  font-size: 0.74rem; font-weight: 600;
  color: #a5b4fc; background: rgba(165,180,252,0.12);
  border: 1px solid rgba(165,180,252,0.25);
  border-radius: 999px; padding: 0.22rem 0.7rem;
}

/* ============================================================
   BUILD SECTION (with real image)
   ============================================================ */
.build-section {
  padding: 7rem 0;
  background: #f8fafc;
}
.build-two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
}
.build-col { }
.build-col-img {
  width: 100%;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 16px 40px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
}
.build-col-img img {
  width: 100%; height: auto; display: block;
}
.build-steps { margin-top: 2rem; display: flex; flex-direction: column; gap: 0; }
.build-step {
  display: flex; gap: 1.2rem; padding: 1.5rem 0;
  border-bottom: 1px solid #e2e8f0;
}
.build-step:last-child { border-bottom: none; }
.build-step-num {
  font-size: 0.85rem; font-weight: 800;
  color: #28b5c1; flex-shrink: 0;
  width: 32px; height: 32px;
  border-radius: 50%; background: #d4f1f4;
  display: flex; align-items: center; justify-content: center;
}
.build-step-title {
  font-size: 0.95rem; font-weight: 700;
  color: #0f172a; margin-bottom: 0.3rem;
}
.build-step-desc {
  font-size: 0.85rem; color: #64748b; line-height: 1.6;
}

/* ============================================================
   INDUSTRY EXPERTISE (with real images)
   ============================================================ */
.industry-section {
  padding: 7rem 0;
  background: #fff;
}
.industry-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}
.industry-card {
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 20px rgba(15,23,42,0.06);
  transition: box-shadow 0.3s, transform 0.3s;
}
.industry-card:hover {
  box-shadow: 0 20px 50px rgba(15,23,42,0.12);
  transform: translateY(-6px);
}
.industry-card-img {
  width: 100%;
  height: 200px;
  overflow: hidden;
  position: relative;
  background: #f8fafc;
  display: flex; align-items: center; justify-content: center;
}
.industry-card-img img {
  height: 140px; width: auto; object-fit: contain;
  transition: transform 0.4s;
}
.industry-card:hover .industry-card-img img { transform: scale(1.08); }
.industry-overlay {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  padding: 0.8rem 1.2rem;
  background: linear-gradient(transparent, rgba(15,23,42,0.7));
}
.industry-tag {
  font-size: 0.8rem; font-weight: 700; color: #fff;
}
.industry-card-body { padding: 1.5rem 2rem 2rem; }
.industry-card-body h4 {
  font-size: 1.1rem; font-weight: 700;
  color: #0f172a; margin-bottom: 0.5rem;
}
.industry-card-body p {
  font-size: 0.9rem; color: #64748b;
  line-height: 1.65; margin-bottom: 1rem;
}
.industry-tags { display: flex; flex-wrap: wrap; gap: 0.4rem; }
.industry-tags span {
  font-size: 0.72rem; font-weight: 600;
  color: #28b5c1; background: #e6f7f9;
  border-radius: 999px; padding: 0.2rem 0.65rem;
}

/* ============================================================
   CASE PREVIEW
   ============================================================ */
.case-preview-section {
  padding: 7rem 0;
  background: #f8fafc;
}
.case-preview-card {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 0 8px 40px rgba(15,23,42,0.08);
  overflow: hidden;
  position: relative;
}
.case-preview-card::before {
  content: '';
  position: absolute; top: 0; left: 0; right: 0;
  height: 4px;
  background: linear-gradient(90deg, #28b5c1, #06b6d4);
}
.case-preview-img {
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0,0,0,0.1);
}
.case-preview-img img { width: 100%; height: auto; display: block; }
.case-preview-body { display: flex; flex-direction: column; justify-content: center; }
.case-badge {
  display: inline-block;
  font-size: 0.75rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.08em;
  color: #16a34a; background: #dcfce7;
  border-radius: 999px; padding: 0.3rem 0.8rem;
  margin-bottom: 1rem; width: fit-content;
}
.case-preview-title {
  font-size: 1.6rem; font-weight: 800;
  color: #0f172a; margin-bottom: 1rem; line-height: 1.25;
}
.case-preview-intro {
  font-size: 0.95rem; color: #475569;
  line-height: 1.7; margin-bottom: 1.5rem;
}
.case-factors-title {
  font-size: 0.95rem; font-weight: 700;
  color: #0f172a; margin-bottom: 0.8rem;
}
.case-factors-list {
  list-style: none; padding: 0; margin-bottom: 2rem;
}
.case-factors-list li {
  display: flex; align-items: flex-start; gap: 0.6rem;
  font-size: 0.88rem; color: #475569;
  line-height: 1.5; margin-bottom: 0.5rem;
}
.check-icon-green { width: 16px; height: 16px; color: #22c55e; flex-shrink: 0; }

/* ============================================================
   CTA
   ============================================================ */
.sol-cta-section {
  padding: 7rem 2rem;
  background: linear-gradient(135deg, #28b5c1 0%, #1e9aa5 60%, #06b6d4 100%);
  text-align: center;
  position: relative;
  overflow: hidden;
}
.sol-cta-inner { max-width: 700px; margin: 0 auto; position: relative; z-index: 1; }
.sol-cta-title {
  font-size: 2.4rem; font-weight: 800;
  color: #fff; line-height: 1.2; margin-bottom: 1rem;
}
.sol-cta-sub {
  font-size: 1.05rem; color: rgba(255,255,255,0.8); margin-bottom: 2.5rem;
}
.sol-cta-btns { display: flex; justify-content: center; flex-wrap: wrap; gap: 1rem; }
.sol-cta-section .btn-primary { background: #fff; color: #28b5c1; box-shadow: 0 4px 20px rgba(0,0,0,0.2); }
.sol-cta-section .btn-primary:hover { background: #f0f4ff; }
.sol-cta-section .btn-ghost { border-color: rgba(255,255,255,0.5); color: #fff; }
.sol-cta-section .btn-ghost:hover { background: rgba(255,255,255,0.1); border-color: #fff; }

/* ============================================================
   RESPONSIVE
   ============================================================ */
@media (max-width: 1024px) {
  .sol-main-grid,
  .unified-grid,
  .industry-grid { grid-template-columns: repeat(2, 1fr); }
  .sol-hero-inner { gap: 2rem; }
  .sol-hero-visual { flex: 0 0 400px; }
  .build-two-col { grid-template-columns: 1fr; }
}
@media (max-width: 768px) {
  .sol-hero-inner { flex-direction: column; padding-bottom: 3rem; }
  .sol-hero-visual { flex: none; width: 100%; }
  .sol-hero-float-card { display: none; }
  .sol-main-grid,
  .unified-grid,
  .industry-grid { grid-template-columns: 1fr; }
  .case-preview-card { grid-template-columns: 1fr; }
  .section-title { font-size: 2rem; }
  .sol-hero-title { font-size: 2.2rem; }
  .sol-cta-title { font-size: 1.9rem; }
}
</style>

